import { useMemo, useState } from 'react';
import { FiChevronDown, FiEdit2, FiGrid, FiList, FiMail, FiPhone, FiPlus, FiSearch, FiStar, FiTrash2, FiUser, FiUsers } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { useCampuses } from '../../contexts/CampusContext';
import { useCellMinistries } from '../../contexts/CellMinistryContext';
import { useLeaders } from '../../contexts/LeaderContext';
import { useMinistries } from '../../contexts/MinistryContext';
import LeaderService from '../../services/leadersService';
import LeaderForm from './forms/LeaderForm';
import ConfirmDialog from './ui/ConfirmDialog';
import EmptyState from './ui/EmptyState';
import Modal from './ui/Modal';

const LeadersManager = () => {
  const { leaders, loading, error, setLeaders } = useLeaders();
  const { campuses } = useCampuses();
  const { ministries } = useMinistries();
  const { cellGroups } = useCellMinistries();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [currentLeader, setCurrentLeader] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('table');
  const [filterType, setFilterType] = useState('all');
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [selectedLeaders, setSelectedLeaders] = useState(new Set());

  // Safe array access helper
  const safeArray = (arr) => Array.isArray(arr) ? arr : [];

  // Normalize leader data
  const normalizeLeader = (leader) => ({
    ...leader,
    role: safeArray(leader.role),
    leaderType: safeArray(leader.leaderType),
    cellgroupId: safeArray(leader.cellgroupId)
  });

  // Enhanced filtering and sorting
  const filteredAndSortedLeaders = useMemo(() => {
    let filtered = leaders.map(normalizeLeader).filter(leader => {
      const matchesSearch = leader.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        leader.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        leader.phone?.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesFilter = filterType === 'all' || 
        leader.leaderType.some(type => 
          typeof type === 'string' ? type.toLowerCase().includes(filterType.toLowerCase()) :
          type.name?.toLowerCase().includes(filterType.toLowerCase())
        );
      
      return matchesSearch && matchesFilter;
    });

    // Sort the filtered results
    filtered.sort((a, b) => {
      let aValue = a[sortBy];
      let bValue = b[sortBy];
      
      if (Array.isArray(aValue)) aValue = aValue[0]?.name || aValue[0] || '';
      if (Array.isArray(bValue)) bValue = bValue[0]?.name || bValue[0] || '';
      
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }
      
      return sortOrder === 'asc' 
        ? aValue > bValue ? 1 : -1
        : aValue < bValue ? 1 : -1;
    });

    return filtered;
  }, [leaders, searchTerm, filterType, sortBy, sortOrder]);

  // Get unique leader types for filter dropdown
  const leaderTypes = useMemo(() => {
    const types = new Set();
    leaders.forEach(leader => {
      safeArray(leader.leaderType).forEach(type => {
        const typeName = typeof type === 'string' ? type : type.name;
        if (typeName) types.add(typeName);
      });
    });
    return Array.from(types);
  }, [leaders]);

  const renderArrayField = (array, options = []) => {
    if (!array || !Array.isArray(array)) return '-';
    
    // If we have options, try to map IDs to names
    if (options.length > 0) {
      array = array.map(item => {
        const found = options.find(opt => opt.id === item || opt.value === item);
        return found ? found.name || found.label : item;
      });
    }

    return (
      <div className="flex flex-wrap gap-1.5">
        {array.map((item, index) => (
          <span 
            key={index}
            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gradient-to-r from-indigo-50 to-purple-50 text-indigo-700 border border-indigo-100"
          >
            {typeof item === 'object' ? item.name || item.title || String(item) : String(item)}
          </span>
        ))}
      </div>
    );
  };

  const handleDeleteClick = (leader) => {
    setCurrentLeader(normalizeLeader(leader));
    setIsDeleteConfirmOpen(true);
  };

  const handleDelete = async () => {
    if (!currentLeader) return;
    
    try {
      setProcessing(true);
      await LeaderService.deleteLeader(currentLeader.id);
      setLeaders(prev => prev.filter(item => item.id !== currentLeader.id));
      toast.success('Leader deleted successfully');
    } catch (error) {
      console.error('Delete leader error:', error);
      toast.error(`Failed to delete leader: ${error.message}`);
    } finally {
      setProcessing(false);
      setIsDeleteConfirmOpen(false);
      setCurrentLeader(null);
    }
  };

  const handleEdit = (leader) => {
    setCurrentLeader(normalizeLeader(leader));
    setIsModalOpen(true);
  };

  const handleSubmit = async (formData) => {
    try {
      setProcessing(true);
      let updatedLeaders;
      
      const normalizedData = {
        ...formData,
        role: safeArray(formData.role),
        leaderType: safeArray(formData.leaderType),
      };

      if (currentLeader) {
        const updated = await LeaderService.updateLeader(currentLeader.id, normalizedData);
        updatedLeaders = leaders.map(l => 
          l.id === currentLeader.id ? normalizeLeader(updated) : normalizeLeader(l)
        );
        toast.success('Leader updated successfully');
      } else {
        const created = await LeaderService.createLeader(normalizedData);
        updatedLeaders = [...leaders, normalizeLeader(created)];
        toast.success('Leader created successfully');
      }
      
      setLeaders(updatedLeaders);
      handleCloseModal();
    } catch (error) {
      console.error('Save leader error:', error);
      toast.error(`Failed to save leader: ${error.message}`);
    } finally {
      setProcessing(false);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setCurrentLeader(null);
  };

  const handleCreateNew = () => {
    setCurrentLeader(null);
    setIsModalOpen(true);
  };

  const handleSelectLeader = (leaderId) => {
    const newSelected = new Set(selectedLeaders);
    if (newSelected.has(leaderId)) {
      newSelected.delete(leaderId);
    } else {
      newSelected.add(leaderId);
    }
    setSelectedLeaders(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedLeaders.size === filteredAndSortedLeaders.length) {
      setSelectedLeaders(new Set());
    } else {
      setSelectedLeaders(new Set(filteredAndSortedLeaders.map(l => l.id)));
    }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="bg-gradient-to-r from-red-50 to-pink-50 border border-red-200 rounded-2xl p-6 shadow-lg">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                  <svg className="h-5 w-5 text-red-600" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-semibold text-red-900">
                  Error loading leaders
                </h3>
                <p className="mt-2 text-red-700">
                  {error.message || 'An unexpected error occurred'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="bg-white/70 backdrop-blur-xl rounded-3xl shadow-xl border border-white/20 p-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            <div className="space-y-2">
              <h1 className="text-4xl font-bold bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-transparent flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg">
                  <FiUsers className="text-white text-xl" />
                </div>
                Leaders Management
              </h1>
              <p className="text-gray-600 text-lg">
                Manage your organization's leadership team with style
              </p>
              <div className="flex items-center gap-4 text-sm text-gray-500">
                <span className="flex items-center gap-1">
                  <FiUsers className="w-4 h-4" />
                  {leaders.length} Total Leaders
                </span>
                <span className="flex items-center gap-1">
                  <FiStar className="w-4 h-4" />
                  {filteredAndSortedLeaders.length} Showing
                </span>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={handleCreateNew}
                className="group relative inline-flex items-center justify-center px-6 py-3 text-sm font-semibold text-white transition-all duration-300 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-2xl shadow-lg hover:shadow-xl hover:scale-105 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                <FiPlus className="mr-2 transition-transform group-hover:rotate-90" />
                Add New Leader
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-indigo-600 to-purple-600 opacity-0 group-hover:opacity-20 transition-opacity"></div>
              </button>
            </div>
          </div>
        </div>

        {/* Search and Filter Bar */}
        <div className="bg-white/70 backdrop-blur-xl rounded-2xl shadow-lg border border-white/20 p-6">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="relative flex-1">
              <FiSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search leaders by name, email, or phone..."
                className="w-full pl-12 pr-4 py-3 bg-white/80 border border-gray-200/50 rounded-xl focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500/50 transition-all duration-300 text-gray-900 placeholder-gray-500"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            
            <div className="flex items-center gap-3">
              <div className="relative">
                <select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  className="appearance-none bg-white/80 border border-gray-200/50 rounded-xl px-4 py-3 pr-10 focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500/50 transition-all duration-300"
                >
                  <option value="all">All Types</option>
                  {leaderTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
                <FiChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
              </div>
              
              <div className="relative">
                <select
                  value={`${sortBy}-${sortOrder}`}
                  onChange={(e) => {
                    const [field, order] = e.target.value.split('-');
                    setSortBy(field);
                    setSortOrder(order);
                  }}
                  className="appearance-none bg-white/80 border border-gray-200/50 rounded-xl px-4 py-3 pr-10 focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500/50 transition-all duration-300"
                >
                  <option value="name-asc">Name A-Z</option>
                  <option value="name-desc">Name Z-A</option>
                  <option value="role-asc">Role A-Z</option>
                  <option value="role-desc">Role Z-A</option>
                </select>
                <FiChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
              </div>
              
              <div className="flex items-center bg-gray-100 rounded-xl p-1">
                <button
                  onClick={() => setViewMode('table')}
                  className={`p-2 rounded-lg transition-all duration-300 ${
                    viewMode === 'table' 
                      ? 'bg-white shadow-sm text-indigo-600' 
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <FiList className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded-lg transition-all duration-300 ${
                    viewMode === 'grid' 
                      ? 'bg-white shadow-sm text-indigo-600' 
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <FiGrid className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Leaders Display */}
        {loading ? (
          <div className="bg-white/70 backdrop-blur-xl rounded-2xl shadow-lg border border-white/20 p-8">
            <div className="space-y-6">
              <div className="animate-pulse space-y-4">
                <div className="h-8 bg-gradient-to-r from-gray-200 to-gray-300 rounded-xl w-1/4"></div>
                <div className="h-6 bg-gradient-to-r from-gray-200 to-gray-300 rounded-lg w-3/4"></div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {[...Array(6)].map((_, i) => (
                    <div key={i} className="h-32 bg-gradient-to-r from-gray-200 to-gray-300 rounded-xl"></div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ) : filteredAndSortedLeaders.length === 0 ? (
          <div className="bg-white/70 backdrop-blur-xl rounded-2xl shadow-lg border border-white/20">
            <EmptyState
              title="No leaders found"
              description={searchTerm ? "Try a different search term or filter" : "Create your first leader to get started"}
              icon={<FiUser className="mx-auto h-16 w-16 text-gray-400" />}
              action={
                <button
                  onClick={handleCreateNew}
                  className="inline-flex items-center px-6 py-3 text-sm font-semibold text-white bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300"
                >
                  <FiPlus className="mr-2" />
                  Add New Leader
                </button>
              }
            />
          </div>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredAndSortedLeaders.map((leader) => (
              <div key={leader.id} className="group bg-white/70 backdrop-blur-xl rounded-2xl shadow-lg border border-white/20 overflow-hidden hover:shadow-2xl hover:scale-105 transition-all duration-300">
                <div className="relative p-6">
                  <div className="flex items-center justify-center mb-4">
                    <div className="relative">
                      <img 
                        className="w-20 h-20 rounded-2xl object-cover border-4 border-white shadow-lg" 
                        src={leader.profile_image || '/api/placeholder/80/80'} 
                        alt={leader.name}
                        onError={(e) => {
                          e.target.src = '/api/placeholder/80/80';
                        }}
                      />
                      <div className="absolute -bottom-2 -right-2 w-8 h-8 bg-gradient-to-r from-green-400 to-emerald-500 rounded-full border-4 border-white shadow-sm flex items-center justify-center">
                        <FiStar className="w-3 h-3 text-white" />
                      </div>
                    </div>
                  </div>
                  
                  <div className="text-center space-y-2">
                    <h3 className="font-semibold text-gray-900 text-lg">{leader.name}</h3>
                    <div className="flex justify-center">
                      {renderArrayField(leader.role)}
                    </div>
                    <div className="flex justify-center">
                      {renderArrayField(leader.leaderType)}
                    </div>
                    <div className="flex justify-center">
                      {renderArrayField(leader.cellgroupId, cellGroups)}
                    </div>
                  </div>
                  
                  <div className="mt-4 space-y-2">
                    {leader.phone && (
                      <div className="flex items-center text-sm text-gray-600">
                        <FiPhone className="mr-2 h-4 w-4 text-gray-400" />
                        {leader.phone}
                      </div>
                    )}
                    {leader.email && (
                      <div className="flex items-center text-sm text-gray-600">
                        <FiMail className="mr-2 h-4 w-4 text-gray-400" />
                        <span className="truncate">{leader.email}</span>
                      </div>
                    )}
                  </div>
                  
                  <div className="mt-6 flex gap-2">
                    <button
                      onClick={() => handleEdit(leader)}
                      className="flex-1 inline-flex items-center justify-center px-3 py-2 text-sm font-medium text-gray-700 bg-white/80 border border-gray-200 rounded-xl hover:bg-gray-50 hover:scale-105 transition-all duration-300"
                    >
                      <FiEdit2 className="mr-1 h-4 w-4" />
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteClick(leader)}
                      className="flex-1 inline-flex items-center justify-center px-3 py-2 text-sm font-medium text-white bg-gradient-to-r from-red-500 to-pink-600 rounded-xl hover:scale-105 transition-all duration-300"
                    >
                      <FiTrash2 className="mr-1 h-4 w-4" />
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white/70 backdrop-blur-xl rounded-2xl shadow-lg border border-white/20 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200/50">
                <thead className="bg-gradient-to-r from-gray-50/80 to-gray-100/80">
                  <tr>
                    <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      <input
                        type="checkbox"
                        checked={selectedLeaders.size === filteredAndSortedLeaders.length && filteredAndSortedLeaders.length > 0}
                        onChange={handleSelectAll}
                        className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                      />
                    </th>
                    <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Profile
                    </th>
                    <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Name
                    </th>
                    <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Role
                    </th>
                    <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Leader Type
                    </th>
                    <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Cell Groups
                    </th>
                    <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Contact
                    </th>
                    <th scope="col" className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white/50 divide-y divide-gray-200/30">
                  {filteredAndSortedLeaders.map((leader) => (
                    <tr key={leader.id} className="hover:bg-white/80 transition-all duration-300 group">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={selectedLeaders.has(leader.id)}
                          onChange={() => handleSelectLeader(leader.id)}
                          className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-12 w-12 relative">
                            <img 
                              className="h-12 w-12 rounded-xl object-cover border-2 border-white shadow-lg group-hover:scale-110 transition-transform duration-300" 
                              src={leader.profile_image || '/api/placeholder/48/48'} 
                              alt={leader.name}
                              onError={(e) => {
                                e.target.src = '/api/placeholder/48/48';
                              }}
                            />
                            <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-gradient-to-r from-green-400 to-emerald-500 rounded-full border-2 border-white"></div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-semibold text-gray-900">{leader.name}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">
                          {renderArrayField(leader.role)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {renderArrayField(leader.leaderType)}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">
                          
                          {renderArrayField(leader.cellgroupId, cellGroups)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex flex-col space-y-1">
                          {leader.phone && (
                            <div className="flex items-center text-sm text-gray-600">
                              <FiPhone className="mr-2 h-4 w-4 text-gray-400" />
                              {leader.phone}
                            </div>
                          )}
                          {leader.email && (
                            <div className="flex items-center text-sm text-gray-600">
                              <FiMail className="mr-2 h-4 w-4 text-gray-400" />
                              <span className="truncate max-w-xs">{leader.email}</span>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end space-x-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                          <button
                            onClick={() => handleEdit(leader)}
                            className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white/80 border border-gray-200 rounded-xl hover:bg-gray-50 hover:scale-105 transition-all duration-300 shadow-sm"
                          >
                            <FiEdit2 className="mr-1 h-4 w-4" />
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteClick(leader)}
                            className="inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-gradient-to-r from-red-500 to-pink-600 rounded-xl hover:scale-105 transition-all duration-300 shadow-sm"
                          >
                            <FiTrash2 className="mr-1 h-4 w-4" />
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Bulk Actions Bar */}
        {selectedLeaders.size > 0 && (
          <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 bg-white/90 backdrop-blur-xl rounded-2xl shadow-2xl border border-white/20 px-6 py-4 flex items-center gap-4 z-50">
            <span className="text-sm font-medium text-gray-700">
              {selectedLeaders.size} leader{selectedLeaders.size > 1 ? 's' : ''} selected
            </span>
            <button
              onClick={() => setSelectedLeaders(new Set())}
              className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
            >
              Clear selection
            </button>
          </div>
        )}

        {/* Edit/Create Modal */}
        <Modal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          title={
            <div className="flex items-center">
              <div className="w-8 h-8 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center mr-3">
                <FiUser className="text-white text-sm" />
              </div>
              {currentLeader ? 'Edit Leader' : 'Create New Leader'}
            </div>
          }
          size="lg"
        >
          <LeaderForm
            initialValues={currentLeader || {
              name: '',
              role: [],
              phone: '',
              email: '',
              profile_image: '',
              leaderType: [],
              campusId: null,
              ministryId: null,
              cellgroupId: []
            }}
            onSubmit={handleSubmit}
            onCancel={handleCloseModal}
            isSubmitting={processing}
            campuses={campuses}
            ministries={ministries}
            cellGroups={cellGroups}
          />
        </Modal>

        {/* Delete Confirmation Dialog */}
        <ConfirmDialog
          isOpen={isDeleteConfirmOpen}
          onClose={() => {
            setIsDeleteConfirmOpen(false);
            setCurrentLeader(null);
          }}
          onConfirm={handleDelete}
          title="Delete Leader"
          message={
            <div className="space-y-4">
              <p className="text-gray-600">
                Are you sure you want to delete this leader?
              </p>
              <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl p-4 border border-gray-200">
                <div className="flex items-center">
                  <div className="relative">
                    <img 
                      className="h-12 w-12 rounded-xl object-cover border-2 border-white shadow-lg" 
                      src={currentLeader?.profile_image || '/api/placeholder/48/48'} 
                      alt={currentLeader?.name}
                    />
                    <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-red-500 rounded-full border-2 border-white"></div>
                  </div>
                  <div className="ml-4">
                    <div className="font-semibold text-gray-900">
                      {currentLeader?.name}
                    </div>
                    <div className="text-sm text-gray-600">
                      {renderArrayField(currentLeader?.role)}
                    </div>
                    <div className="text-sm text-gray-600 mt-1">
                      {renderArrayField(currentLeader?.cellgroupId, cellGroups)}
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gradient-to-r from-red-50 to-pink-50 border border-red-200 rounded-xl p-3">
                <p className="text-sm text-red-700 font-medium flex items-center">
                  <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  This action cannot be undone.
                </p>
              </div>
            </div>
          }
          processing={processing}
        />
      </div>
    </div>
  );
};

export default LeadersManager;